import React, { useEffect, useMemo, useRef, useState } from "react";

import toastError from "../../errors/toastError";
import api from "../../services/api";

function FunnelBadge({ item, onClick, onRemove }) {
	const color = item.color || "#64748b";

	return (
		<span className="tailwick-contact-drawer__funnel-badge" style={{ backgroundColor: color }}>
			<button type="button" className="tailwick-contact-drawer__funnel-badge-label" onClick={onClick}>
				{item.name}
			</button>
			<button
				type="button"
				className="tailwick-contact-drawer__funnel-badge-remove"
				onClick={onRemove}
				aria-label="Remover"
			>
				×
			</button>
		</span>
	);
}

export default function ContactDrawerFunnel({ contact }) {
	const containerRef = useRef(null);
	const [salesFunnels, setSalesFunnels] = useState([]);
	const [funnels, setFunnels] = useState([]);
	const [selecteds, setSelecteds] = useState([]);
	const [pickerOpen, setPickerOpen] = useState(false);
	const [filters, setFilters] = useState({
		salesFunnelId: undefined,
		salesFunnelColumnId: undefined,
	});
	const [searchTerm, setSearchTerm] = useState("");

	useEffect(() => {
		(async () => {
			try {
				const saleFunnel = await api.get("/sales-funnel");
				const salesfunnel = await Promise.all(
					saleFunnel.data.map(async (funnel) => {
						const { data } = await api.get("/sales-funnel/column", {
							params: {
								workspace: "contacts",
								salesFunnelId: funnel.id,
							},
						});
						return { ...funnel, columns: data };
					})
				);
				setSalesFunnels(salesfunnel);
			} catch (error) {
				toastError(error);
			}
		})();
	}, []);

	useEffect(() => {
		const funnelsOptions = salesFunnels.flatMap((funnel) =>
			funnel.columns.map((column) => ({
				idColumn: column.id,
				idFunnel: funnel.id,
				name: `${funnel.name} - ${column.name}`,
				color: column.cardColor,
			}))
		);
		setFunnels(funnelsOptions);
	}, [salesFunnels]);

	useEffect(() => {
		const funnelsContact = contact?.funnels;
		if (!funnelsContact) return;

		const mapped = funnelsContact.map((funnel) => ({
			id: funnel.FunnelContact.id,
			salesFunnelColumnId: funnel.FunnelContact.salesFunnelId,
			salesFunnelId: funnel.salesFunnel.id,
			name: `${funnel.salesFunnel.name} - ${funnel.name}`,
			color: funnel.cardColor,
		}));
		setSelecteds(mapped);
	}, [contact]);

	useEffect(() => {
		if (!pickerOpen) return undefined;

		const handleClickOutside = (event) => {
			if (containerRef.current && !containerRef.current.contains(event.target)) {
				closePicker();
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [pickerOpen]);

	const closePicker = () => {
		setPickerOpen(false);
		setFilters({ salesFunnelId: undefined, salesFunnelColumnId: undefined });
		setSearchTerm("");
	};

	const handleCardMove = async (toSalesFunnelColumnsId, toSalesFunnelId) => {
		await api.post("/sales-funnel/pin-contact", {
			contactId: contact.id,
			salesFunnelColumnId: toSalesFunnelColumnsId,
			salesFunnelId: toSalesFunnelId,
		});
	};

	const handleChangeFunnel = async (value) => {
		await handleCardMove(value.idColumn, value.idFunnel);

		if (filters.salesFunnelId) {
			setSelecteds((prev) =>
				prev.map((item) =>
					item.salesFunnelColumnId === filters.salesFunnelColumnId
						? {
								...item,
								salesFunnelColumnId: value.idColumn,
								salesFunnelId: value.idFunnel,
								name: value.name,
								color: value.color,
							}
						: item
				)
			);
		} else {
			setSelecteds((prev) => [
				...prev,
				{
					id: contact.id,
					salesFunnelColumnId: value.idColumn,
					salesFunnelId: value.idFunnel,
					name: value.name,
					color: value.color,
				},
			]);
		}

		closePicker();
	};

	const handleUnpin = async (item) => {
		try {
			await api.post("/sales-funnel/unpin-contact", {
				contactId: contact.id,
				salesFunnelColumnId: item.salesFunnelColumnId,
				salesFunnelId: item.salesFunnelId,
			});
			setSelecteds((prev) =>
				prev.filter((entry) => entry.salesFunnelColumnId !== item.salesFunnelColumnId)
			);
		} catch (error) {
			toastError(error);
		}
	};

	const filteredFunnels = useMemo(() => {
		const normalized = searchTerm.trim().toLowerCase();
		return (funnels ?? []).filter((funnel) => {
			if (filters.salesFunnelId) {
				if (funnel.idFunnel !== filters.salesFunnelId) return false;
				if (funnel.idColumn === filters.salesFunnelColumnId) return false;
			} else if (
				selecteds.some((selected) => selected.salesFunnelId === funnel.idFunnel)
			) {
				return false;
			}

			if (!normalized) return true;
			return funnel.name.toLowerCase().includes(normalized);
		});
	}, [funnels, filters, selecteds, searchTerm]);

	const openAddPicker = () => {
		setFilters({ salesFunnelId: undefined, salesFunnelColumnId: undefined });
		setPickerOpen(true);
	};

	const openEditPicker = (item) => {
		setFilters({
			salesFunnelId: item.salesFunnelId,
			salesFunnelColumnId: item.salesFunnelColumnId,
		});
		setPickerOpen(true);
	};

	return (
		<div className="tailwick-contact-drawer__section" ref={containerRef}>
			<div className="tailwick-contact-drawer__section-head">
				<p className="tailwick-filter-panel__section-title mb-0">Funil de vendas</p>
				<button
					type="button"
					className="btn size-7 bg-default-150 text-default-600 hover:bg-default-200 hover:text-primary"
					onClick={openAddPicker}
					aria-label="Adicionar funil"
				>
					<svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
						<path d="M6 9l6 6 6-6" />
					</svg>
				</button>
			</div>

			{selecteds.length > 0 && (
				<div className="tailwick-contact-drawer__funnel-list">
					{selecteds.map((item) => (
						<FunnelBadge
							key={`${item.salesFunnelId}-${item.salesFunnelColumnId}`}
							item={item}
							onClick={() => openEditPicker(item)}
							onRemove={() => handleUnpin(item)}
						/>
					))}
				</div>
			)}

			{pickerOpen && (
				<div className="tailwick-contact-drawer__picker mt-2">
					<input
						type="text"
						className="form-input form-input-sm w-full"
						placeholder="Buscar funil..."
						value={searchTerm}
						onChange={(e) => setSearchTerm(e.target.value)}
						autoFocus
					/>
					<div className="tailwick-tags-multi__list mt-2">
						{filteredFunnels.map((funnel) => (
							<button
								key={`${funnel.idFunnel}-${funnel.idColumn}`}
								type="button"
								className="tailwick-tags-multi__option w-full"
								onClick={() => handleChangeFunnel(funnel)}
							>
								<span
									className="tailwick-filter-panel__color-dot"
									style={{ backgroundColor: funnel.color || "#64748b" }}
								/>
								<span className="tailwick-tags-multi__option-label">{funnel.name}</span>
							</button>
						))}
						{filteredFunnels.length === 0 && (
							<p className="tailwick-filter-panel__empty">—</p>
						)}
					</div>
				</div>
			)}
		</div>
	);
}
